import { ref } from 'vue';
import { useStore } from 'vuex';
import { SfTodoSyncSettings } from '@/api/openapi';
import { SfTodoSyncSettingsService } from '@/api/user/resources/salesforce/sf_todo_sync_settings';
import { key } from '@/store/index';

type TodoType = 'call_result' | 'free_action';

// * getSfTodoSyncSettings のモジュールスコープキャッシュ。
let cachedSfTodoSyncSettingsPromise: Promise<SfTodoSyncSettings[] | null> | null = null;

const fetchSfTodoSyncSettings = (): Promise<SfTodoSyncSettings[] | null> => {
  if (cachedSfTodoSyncSettingsPromise === null) {
    const sfApi = new SfTodoSyncSettingsService();
    cachedSfTodoSyncSettingsPromise = sfApi.getSfTodoSyncSettings()
      .then((res) => res.data ?? null)
      .catch(() => null);
  }
  return cachedSfTodoSyncSettingsPromise;
};

/**
 * SF TODO連携の注意書き表示判定 composable
 *
 * Salesforce連携機能が有効かつSalesforce接続済み（connectStatus === 'connected'）、
 * かつ指定したtodoTypeの連携設定がONの場合に `showSfSyncNotice` が true になる。
 *
 * サーバー側の SfTodoSyncSetting.salesforce_link_active? は
 * available_features の salesforce_link フラグに加えて
 * salesforce_setting.connect_status == :connected も確認しているため、
 * クライアント側でも同様に接続状態を確認する。
 */
const useSfTodoSyncNotice = (todoType: TodoType) => {
  const store = useStore(key);
  const showSfSyncNotice = ref(false);

  const load = async () => {
    if (!store) return;
    // availableFeatures と salesforceSetting がまだロードされていない場合は dispatch して取得する
    // getAvailableFeaturesAction / getSalesforceSettingAction は Vuex の waiting により
    // 同時に複数呼ばれても実リクエストは1回に抑制される
    await Promise.all([
      store.dispatch('user/getAvailableFeaturesAction'),
      store.dispatch('user/getSalesforceSettingAction'),
    ]);
    const availableFeatures = store.getters['user/availableFeatures'];
    const isSalesforceLinkEnabled = !!availableFeatures?.feature?.salesforce_link;
    if (!isSalesforceLinkEnabled) return;

    // サーバー側と同様に接続状態も確認する（token_expired 等の場合は実際には同期されないため）
    const salesforceSetting = store.getters['user/salesforceSetting'];
    const isSalesforceConnected = salesforceSetting?.connectStatus === 'connected';
    if (!isSalesforceConnected) return;

    const settings = await fetchSfTodoSyncSettings();
    if (settings === null) {
      // 取得失敗時は注意書きを表示しない
      showSfSyncNotice.value = false;
      return;
    }
    const setting = settings.find((s: SfTodoSyncSettings) => s.todoType === todoType);
    // サーバー側と同じ仕様: レコードが存在しない場合はデフォルトON（true）
    showSfSyncNotice.value = setting ? setting.active ?? true : true;
  };

  // 即時実行（setup() 呼び出し時に非同期で取得開始）
  void load();

  return { showSfSyncNotice };
};

export { useSfTodoSyncNotice };
